export const projects = [
    {
    "id": "ticker",
    "title": "Ticker",
    "slug": "ticker",
    "tagline": "AI-assisted stock ticker extraction and sentiment labeling from Reddit posts, delivered in a mobile feed.",
    "featured": false,
    "category": ["Mobile", "ML", "Full-stack"],
    "stack": [
      "Python",
      "FastAPI",
      "PyTorch",
      "React Native",
      "Expo"
    ],
    "links": {
      "demo": null,
      "code": "https://github.com/chrisdten3/ticker",
      "caseStudy": null
    },
    "problem": "Stock-related discussion on Reddit is high-signal but noisy—it's hard to quickly identify which tickers are being discussed and whether the overall tone implies bullish or bearish sentiment without manually reading dozens of posts.",
    "approach": [
      "Designed and implemented an end-to-end NLP classification pipeline for predicting short-term stock sentiment from Reddit discussions",
      "Collected and filtered r/stocks posts in real time, extracting referenced tickers from unstructured text",
      "Encoded posts using FinBERT embeddings and trained a custom logistic-regression classifier to predict directional signals (up/down/neutral)",
      "Deployed the trained model for online inference, persisting predictions to Supabase with deduplication guarantees",
      "Served predictions via FastAPI and surfaced them in a React Native mobile feed with date-based filtering and deep links to source posts"
    ]
,
    "impact": [
      "Turns unstructured Reddit discussion into a searchable, date-filterable stream of ticker-tagged items",
      "Reduces manual scanning by surfacing extracted tickers alongside a simple up/down/neutral label",
      "Creates a reusable data layer (Supabase) that can power multiple clients and future analytics (watchlists, ranking, alerts)"
    ],
    image: "ticker.png",
    screenshots: ["ticker.png"],
  }, 
    {
    id: "tally",
    title: "Tally",
    slug: "tally",
    tagline: "Payments and treasury infrastructure for student clubs and organizations",
    featured: true,
    category: ["Full-stack"],
    stack: [
      "TypeScript",
      "Next.js",
      "Supabase",
      "Stripe",
    ],
    links: {
      demo: "https://www.jointally.dev/",
      code: null,
      caseStudy: null,
    },
    problem:
      "Student clubs rely on Venmo, spreadsheets, and manual tracking to manage dues and events, leading to lost money, poor transparency, and admin burnout.",
    approach: [
      "Built a unified payments platform using Stripe to handle dues, events, and payouts",
      "Implemented a centralized ledger to track balances per club and member",
      "Designed admin dashboards for real-time financial visibility and reporting",
      "Integrated mobile-first flows for member payments and registration",
      "Automated compliance and fund routing with Stripe Express accounts",
    ],
    impact: [
      "Eliminates manual reconciliation and spreadsheet-based tracking",
      "Improves financial transparency for club leaders and members",
      "Scales from small student groups to large campus organizations",
    ],
    image: "tally.png",
    screenshots: ["tally.png"],
  },
  {
    id: "guia",
    title: "Guia",
    slug: "guia",
    tagline: "AI-powered codebase onboarding for engineering teams",
    featured: false,
    category: ["ML", "Full-stack"],
    stack: ["React", "Python", "OpenAI", "LangChain", "TypeScript"],
    links: {
      demo: null,
      code: null,
      caseStudy: null,
    },
    problem: "Senior engineers spend weeks onboarding new hires, slowing team velocity and creating knowledge bottlenecks.",
    approach: [
      "Built a proof-of-concept AI onboarding platform combining static code analysis, embeddings, and retrieval",
      "Indexed repositories into hierarchical representations to expose architectural structure and dependency flows",
      "Implemented retrieval-augmented Q&A to answer codebase-specific questions with grounded context",
      "Designed adaptive onboarding sequences to demonstrate role-aware and experience-aware learning paths",
    ],
    impact: [
      "Showcased how RAG and code embeddings can support scalable onboarding without continuous senior engineer involvement",
      "Produced a working prototype suitable for future user studies and enterprise validation",
    ],
    image: "guia.png",
    screenshots: ["guia.png"],
  },
  {
    id: "charts",
    title: "Charts",
    slug: "charts",
    tagline: "Portfolio optimization with efficient frontier visualization",
    featured: true,
    category: ["Full-stack", "ML"],
    stack: ["React", "Python", "Flask", "Pandas", "NumPy"],
    links: {
      demo: "https://charts-app-rpxz.onrender.com/",
      code: null,
      caseStudy: null,
    },
    problem: "Individual investors struggle to optimize asset allocation without expensive financial advisors.",
    approach: [
      "Implemented Modern Portfolio Theory for optimal asset distribution",
      "Built interactive efficient frontier visualization",
      "Created real-time portfolio analysis with risk/return metrics",
      "Designed intuitive UI for asset weight adjustments",
    ],
    impact: [
      "Helps users maximize returns for given risk tolerance",
      "Democratizes portfolio optimization tools",
      "Live demo with real market data",
    ],
    image: "charts.png",
    screenshots: ["charts.png"],
  },
  {
    id: "foodme",
    title: "FoodMe",
    slug: "foodme",
    tagline: "Top 100 Google Solution Challenge finalist app",
    featured: true,
    category: ["Mobile", "Full-stack"],
    stack: ["Flutter", "Dart", "Firebase", "Google Maps API"],
    links: {
      demo: null,
      code: null,
      caseStudy: "https://cs.georgetown.edu/news-story/cs-students-advance-to-top-100-teams-in-google-competition/",
    },
    problem: "Food insecurity affects millions while excess food goes to waste daily.",
    approach: [
      "Designed and developed a cross-platform iOS/Android mobile application in Flutter for the Google Student Developer Solution Challenge",
      "Framed the product around select UN Sustainable Development Goals, focusing on food access, redistribution, and waste reduction",
      "Built end-to-end functionality to connect individual food suppliers (e.g., donors, restaurants) with nearby demand in real time",
      "Integrated Google Maps APIs to enable geospatial discovery, proximity-based matching, and location-aware listings",
      "Implemented Firebase for authentication, real-time data synchronization, and backend infrastructure",
      "Piloted and tested the application in the Georgetown University and surrounding Washington, D.C. area to validate local food matching workflows",
    ],
    impact: [
      "Selected as a Global Finalist and Top 3 submission in the United States for the Google Student Developer Solution Challenge",
    ],
    image: "foodme.png",
    screenshots: ["foodme.png"],
  },
  {
    id: "spotify-player",
    title: "Spotify Stats + Player",
    slug: "spotify-player",
    tagline: "In-browser Spotify player with listening analytics",
    featured: false,
    category: ["Full-stack"],
    stack: ["React", "TailwindCSS", "Node.js", "Spotify API"],
    links: {
      demo: "https://chrisdten3.github.io/music-player/",
      code: null,
      caseStudy: null,
    },
    problem: "Spotify's native stats are limited and the web player lacks personalized insights.",
    approach: [
      "Integrated Spotify Web API for authentication and playback",
      "Built listening history analytics and visualization",
      "Created seamless in-browser music player experience",
      "Designed responsive UI with TailwindCSS",
    ],
    impact: [
      "Provides deep insights into listening habits",
      "Full playback control in browser",
      "Live demo available",
    ],
    image: "spotify-player.png",
    screenshots: ["spotify-player.png"],
  },
  {
    id: "movie-recommender",
    title: "Movie Recommender",
    slug: "movie-recommender",
    tagline: "ML-powered movie recommendations using Word2Vec",
    featured: false,
    category: ["ML"],
    stack: ["Python", "Word2Vec", "Gensim", "Streamlit"],
    links: {
      demo: "https://chrisdten3-springcapstonefrontend-frontend-063sr5.streamlit.app/",
      code: null,
      caseStudy: null,
    },
    problem: "Generic movie recommendations fail to capture nuanced user preferences.",
    approach: [
      "Trained Word2Vec embeddings on movie descriptions",
      "Built semantic similarity matching for recommendations",
      "Created interactive Streamlit frontend",
      "Developed for Hoyalytics Analytics Divison Spring Capstone",
    ],
    impact: [
      "Semantic understanding of movie content",
      "Personalized recommendations from natural language",
      "Presented at Hoyalytics Capstone",
    ],
    image: "movie.png",
    screenshots: ["movie.png"],
  },
  {
    id: "hey",
    title: "Hey - Medication Assistant",
    slug: "hey",
    tagline: "iOS medication tracking and reminder app",
    featured: false,
    category: ["Mobile"],
    stack: ["Swift", "SwiftUI", "Combine", "Foundation"],
    links: {
      demo: null,
      code: "https://github.com/chrisdten3/Hey---Medication-Assistant",
      caseStudy: null,
    },
    problem: "Medication non-adherence is a major health issue, especially for patients with complex regimens.",
    approach: [
      "Built native iOS app with SwiftUI",
      "Implemented local notifications for reminders",
      "Designed intuitive medication logging interface",
      "Used Combine for reactive state management",
    ],
    impact: [
      "Helps users maintain medication schedules",
      "Clean, accessible iOS interface",
      "Open source on GitHub",
    ],
    image: "hey.png",
    screenshots: ["hey.png"],
  },
  {
    id: "where2next",
    title: "Where2Next",
    slug: "where2next",
    tagline: "RAG-powered medical symptom advisor",
    featured: false,
    category: ["ML", "Full-stack"],
    stack: ["Python", "RAG", "LangChain", "Web Scraping"],
    links: {
      demo: null,
      code: "https://github.com/CharlieA8/h2ai-testing",
      caseStudy: null,
    },
    problem: "Finding appropriate medical care based on symptoms is confusing and time-consuming.",
    approach: [
      "Implemented RAG for specialized medical advice",
      "Built web scraper for local doctor discovery",
      "Created conversational symptom assessment interface",
      "Integrated location-based provider search",
    ],
    impact: [
      "Connects symptoms to appropriate care",
      "Finds local specialists automatically",
      "Reduces healthcare navigation friction",
    ],
    image: "h2Ai.png",
    screenshots: ["h2Ai.png"],
  },
];

export const otherProjects = [
  {
    id: "epistemologies",
    title: "Race-Based Epistemologies in Tech",
    description: "A report on the effects of white ignorance on technological innovations and the intrinsic biases that arise from improper racial representation.",
    link: "/RaceBasedEpistemologies.pdf",
    isDownloadable: true,
  },
  {
    id: "lengua",
    title: "Lengua.io Medium Article",
    description: "An introduction to Lengua.io, a search engine for language learners built on top of Gensim and Word2Vec.",
    link: "https://medium.com/hoyalytics/lengua-io-and-an-introduction-to-word2vec-6338ed74f91a",
    isDownloadable: false,
  },
];

export const courseGroups = [
  {
    id: "ml-data",
    title: "ML & Data",
    description: "Machine learning and data science",
    courses: [
      { name: "Introduction to Machine Learning", description: "Search algorithms, bayesian networks, neural networks, reinforcement learning" },
      { name: "Deep Learning", description: "Advanced neural network architectures and optimization" },
      { name: "Applied Time Series Analysis", description: "Time series forecasting and modeling" },
      { name: "Intro to Mathematical Statistics", description: "Probability, hypothesis testing, regression" },
      { name: "Data Science & Statistical learning", description: "Supervised and unsupervised learning, statistical inference" },
      { name: "Deep Reinforcement Learning", description: "Advanced RL techniques and applications" },
    ],
  },
  {
    id: "systems",
    title: "Systems",
    description: "Computer science fundamentals",
    courses: [
      { name: "Data Structures", description: "Implementation and analysis of fundamental structures" },
      { name: "Algorithms", description: "Complexity analysis and problem-solving strategies" },
      { name: "Programming Languages", description: "Syntax, grammar, parsing, and semantics" },
      { name: "Advanced Programming", description: "OOP, inheritance, design patterns in Java" },
      { name: "Operating Systems", description: "Processes, memory management, file systems" },
      { name: "Programming Languages", description: "Formal languages, automata, computability" },
    ],
  },
  {
    id: "math",
    title: "Mathematics",
    description: "Mathematical foundations",
    courses: [
      { name: "Linear Algebra", description: "Matrix algebra, vector spaces, eigenvalues" },
      { name: "Discrete Math", description: "Logic, set theory, combinatorics, graph theory" },
      { name: "Multi-Variable Calculus", description: "Partial derivatives, multiple integrals" },
      { name: "Ordinary Differential Equations", description: "First and second-order equations" },
      { name: "Introduction to Proofs", description: "Techniques of mathematical proof and reasoning" },
      { name: "Real Analysis", description: "Numerical methods for solving mathematical problems" },
      { name: "Abstract Algebra", description: "Groups, rings, fields, and their applications" },
    ],
  },
];

export const skills = [
  {
    title: "Languages",
    items: ["Python", "JavaScript", "TypeScript", "Java", "C++", "Swift", "Dart", "SQL"],
  },
  {
    title: "Frameworks & Tools",
    items: ["React", "Next.js", "Node.js", "Flask", "Flutter", "TailwindCSS", "Firebase", "AWS"],
  },
  {
    title: "ML & Data",
    items: ["TensorFlow", "PyTorch", "Pandas", "NumPy", "Gensim", "LangChain", "Streamlit", "Matplotlib"],
  },
];

export const proofStrip = [
  { label: "JPM AI/ML", sublabel: "Incoming" },
  { label: "NLP + ML Systems", sublabel: null },
  { label: "React + Python", sublabel: null },
  { label: "Top 100 Google Solution Challenge", sublabel: null },
];

export const about = {
  headline: "CS + Math at Georgetown",
  positioning: "I build ML-driven data products and developer tools that turn complex systems into usable, intuitive experiences.",
  bullets: [
    "Focused on ML systems, data infrastructure, and applied modeling",
    "Experience building end-to-end products from data ingestion to user-facing interfaces",
    "Open to conversations around Machine Learning, Software Engineering, and Quantitative Finance",
  ],
};


export const contact = {
  email: "cdt50@georgetown.edu",
  linkedin: "https://www.linkedin.com/in/christopher-tengey-12555b24a/",
  github: "https://github.com/chrisdten3",
  resume: "/ChristopherTengey_Winter_25_Resume.pdf",
};
